import React, { useState, useEffect } from 'react';
import {
  Card,
  CardActionArea,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Alert,
  Skeleton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  People as PeopleIcon,
  AccountBalanceWallet as MoneyIcon,
  CheckCircle as CheckIcon,
  ChevronRight as ChevronRightIcon,
  RadioButtonUnchecked as OpenTaskIcon,
  WarningAmber as WarningIcon,
  Celebration as CelebrationIcon,
} from '@mui/icons-material';
import { format, differenceInCalendarDays } from 'date-fns';
import { hu } from 'date-fns/locale';
import weddingEventService from '../../services/weddingEventService';
import { formatHuf, formatHufCompact } from '../../utils/currency';

const PRIORITY_LABEL = { URGENT: 'Sürgős', HIGH: 'Magas', MEDIUM: 'Közepes', LOW: 'Alacsony' };
const PRIORITY_COLOR = { URGENT: 'error', HIGH: 'warning', MEDIUM: 'default', LOW: 'default' };

// Egységes kártya: ikon-jelvény + cím, nagy érték, alcím, sáv, lábléc — az egész kattintható
function StatCard({ icon, color, title, value, subtitle, progress, footer, onClick }) {
  const theme = useTheme();
  const main = theme.palette[color].main;
  const body = (
    <Box sx={{ p: { xs: 2, sm: 2.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(main, 0.12), color: main,
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 20 } })}
        </Box>
        <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ flex: 1 }}>
          {title}
        </Typography>
        {onClick && <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 20 }} />}
      </Box>
      <Typography sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: { xs: '1.6rem', sm: '1.9rem' }, lineHeight: 1.15, color: 'primary.dark' }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 1.5 }}>
        {subtitle}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={Math.min(progress, 100)}
        color={color}
        sx={{ height: 6, borderRadius: 3, bgcolor: alpha(main, 0.12), '& .MuiLinearProgress-bar': { borderRadius: 3 } }}
      />
      {footer && <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>{footer}</Box>}
    </Box>
  );
  return (
    <Card sx={cardSx(theme)}>
      {onClick ? <CardActionArea onClick={onClick} sx={{ height: '100%', alignItems: 'stretch' }}>{body}</CardActionArea> : body}
    </Card>
  );
}

const cardSx = (theme) => ({
  borderRadius: '16px',
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: 'background.paper',
  boxShadow: `0 1px 2px ${alpha(theme.palette.primary.dark, 0.06)}, 0 4px 14px ${alpha(theme.palette.primary.dark, 0.06)}`,
  height: '100%',
  transition: 'box-shadow 0.15s, transform 0.15s',
  '&:hover': { boxShadow: `0 6px 20px ${alpha(theme.palette.primary.dark, 0.12)}` },
});

function SectionCard({ title, action, onAction, children }) {
  const theme = useTheme();
  return (
    <Card sx={{ ...cardSx(theme), '&:hover': undefined, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: 1, display: 'flex', alignItems: 'center' }}>
        <Typography variant="subtitle2" fontWeight={700} color="primary.dark" sx={{ flex: 1 }}>{title}</Typography>
        {action && (
          <Typography
            component="button"
            onClick={onAction}
            variant="caption"
            sx={{ border: 0, bgcolor: 'transparent', cursor: 'pointer', color: 'primary.main', fontWeight: 600, p: 0.5, font: 'inherit', fontSize: '0.8rem' }}
          >
            {action} →
          </Typography>
        )}
      </Box>
      <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: 2, flex: 1 }}>{children}</Box>
    </Card>
  );
}

function EventDashboardTab({ event, onTabChange }) {
  const theme = useTheme();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        const response = await weddingEventService.getEventSummary(event.id);
        if (response.success) setSummary(response.data);
        else setError('Nem sikerült betölteni az összegzést.');
      } catch (err) {
        console.error('Fetch summary error:', err);
        setError('Hiba történt az összegzés betöltése során.');
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [event.id]);

  const gridSx = { display: 'grid', gap: { xs: 1.5, sm: 2 }, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } };

  if (loading) {
    // Váz a végleges elrendezés méreteivel, hogy betöltéskor ne ugráljon a tartalom
    return (
      <Box>
        <Skeleton variant="rounded" height={84} sx={{ borderRadius: '16px', mb: 2 }} />
        <Box sx={gridSx}>
          {[0, 1, 2].map(i => <Skeleton key={i} variant="rounded" height={190} sx={{ borderRadius: '16px' }} />)}
        </Box>
      </Box>
    );
  }
  if (error) return <Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert>;

  const budget = summary?.budget || {};
  const tasks = summary?.tasks || {};
  const guests = summary?.guests || {};

  // Vendégszámok kísérőkkel
  const guestOnly = guests.guestCount ?? (guests.total || 0);
  const companions = guests.plusOnes || 0;
  const totalPeople = guestOnly + companions;
  const responded = guestOnly - (guests.noResponse || 0);
  const rsvpProgress = guestOnly > 0 ? (responded / guestOnly) * 100 : 0;

  // Költségvetés: a keret (ha van) a viszonyítás, különben a tervezett tételek összege
  const limit = Number(event.totalBudget) || 0;
  const planned = budget.totalBudgeted || 0;
  const paid = budget.totalPaid || 0;
  const base = limit > 0 ? limit : planned;
  const paidProgress = base > 0 ? (paid / base) * 100 : 0;

  const taskProgress = tasks.total > 0 ? (tasks.completed / tasks.total) * 100 : 0;
  const daysLeft = differenceInCalendarDays(new Date(event.eventDate), new Date());

  const categories = Object.entries(budget.byCategory || {})
    .map(([name, d]) => ({ name, ...d }))
    .sort((a, b) => b.budgeted - a.budgeted);

  const warnings = [
    tasks.overdue > 0 && `${tasks.overdue} feladat határideje lejárt`,
    limit > 0 && planned > limit && `A tervezett tételek (${formatHuf(planned)}) meghaladják a keretet`,
    event.estimatedGuests && guests.attending > event.estimatedGuests && `Több a megerősített vendég (${guests.attending}), mint a tervezett létszám (${event.estimatedGuests})`,
  ].filter(Boolean);

  return (
    <Box>
      {/* ── Visszaszámláló sáv ── */}
      <Card sx={{
        ...cardSx(theme), '&:hover': undefined, mb: 2,
        background: `linear-gradient(120deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        color: '#fff', border: 0,
      }}>
        <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.75, sm: 2.25 }, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <CelebrationIcon sx={{ color: 'secondary.main', fontSize: 32 }} />
          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
            <Typography sx={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: { xs: '1.35rem', sm: '1.6rem' }, lineHeight: 1.2 }}>
              {daysLeft > 0 ? `Még ${daysLeft} nap a nagy napig` : daysLeft === 0 ? 'Ma van a nagy nap!' : 'Az esküvő lezajlott'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {format(new Date(event.eventDate), 'yyyy. MMMM d. (EEEE)', { locale: hu })}
              {event.venueName ? ` · ${event.venueName}` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: { xs: 2.5, sm: 3.5 } }}>
            {[
              { v: `${Math.round(taskProgress)}%`, l: 'teendő kész' },
              { v: `${Math.round(rsvpProgress)}%`, l: 'visszajelzett' },
            ].map(x => (
              <Box key={x.l} sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.1, color: 'secondary.main' }}>{x.v}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, whiteSpace: 'nowrap' }}>{x.l}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Card>

      {warnings.length > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2, borderRadius: '12px', '& .MuiAlert-message': { width: '100%' } }}>
          {warnings.map(w => <Box key={w}>{w}</Box>)}
        </Alert>
      )}

      {/* ── Fő mutatók ── */}
      <Box sx={{ ...gridSx, mb: 2 }}>
        <StatCard
          icon={<MoneyIcon />}
          color="primary"
          title="Költségvetés"
          value={formatHuf(paid)}
          subtitle={limit > 0 ? `kifizetve a ${formatHufCompact(limit)} keretből` : `kifizetve a ${formatHufCompact(planned)} tervezettből`}
          progress={paidProgress}
          onClick={onTabChange && (() => onTabChange('budget'))}
          footer={
            <>
              <Typography variant="caption" color="text.secondary">Tervezett: <strong>{formatHufCompact(planned)}</strong></Typography>
              {limit > 0 && (
                <Typography variant="caption" color={limit - planned < 0 ? 'error.main' : 'success.main'} sx={{ ml: 'auto', fontWeight: 600 }}>
                  {limit - planned >= 0 ? `${formatHufCompact(limit - planned)} szabad` : `${formatHufCompact(planned - limit)} túllépés`}
                </Typography>
              )}
            </>
          }
        />
        <StatCard
          icon={<CheckIcon />}
          color="success"
          title="Feladatok"
          value={`${tasks.completed || 0} / ${tasks.total || 0}`}
          subtitle={tasks.total ? 'feladat teljesítve' : 'még nincs feladat'}
          progress={taskProgress}
          onClick={onTabChange && (() => onTabChange('tasks'))}
          footer={
            <>
              <Chip size="small" variant="outlined" label={`${tasks.pending || 0} folyamatban`} />
              {tasks.overdue > 0 && <Chip size="small" color="error" variant="outlined" label={`${tasks.overdue} lejárt`} />}
            </>
          }
        />
        <StatCard
          icon={<PeopleIcon />}
          color="info"
          title="Vendégek"
          value={`${totalPeople} fő`}
          subtitle={`${guestOnly} vendég + ${companions} kísérő${event.estimatedGuests ? ` · tervezett: ${event.estimatedGuests} fő` : ''}`}
          progress={rsvpProgress}
          onClick={onTabChange && (() => onTabChange('guests'))}
          footer={
            <>
              <Chip size="small" color="success" variant="outlined" label={`Igen ${guests.attending || 0}`} />
              <Chip size="small" color="warning" variant="outlined" label={`Talán ${guests.maybe || 0}`} />
              <Chip size="small" color="error" variant="outlined" label={`Nem ${guests.notAttending || 0}`} />
              <Chip size="small" variant="outlined" label={`Vár ${guests.noResponse || 0}`} />
            </>
          }
        />
      </Box>

      {/* ── Részletek ── */}
      <Box sx={{ display: 'grid', gap: { xs: 1.5, sm: 2 }, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
        <SectionCard title="Következő teendők" action={tasks.total ? 'Összes' : 'Új feladat'} onAction={() => onTabChange?.('tasks')}>
          {(tasks.upcoming || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              {tasks.total ? 'Minden feladat kész — szép munka!' : 'Még nincs feladat. Vedd fel az első teendőt a Feladatok fülön.'}
            </Typography>
          ) : (
            tasks.upcoming.map((t, i) => {
              const days = t.dueDate ? differenceInCalendarDays(new Date(t.dueDate), new Date()) : null;
              const overdue = days !== null && days < 0;
              return (
                <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, borderTop: i ? `1px solid ${theme.palette.divider}` : 0 }}>
                  <OpenTaskIcon sx={{ fontSize: 18, color: overdue ? 'error.main' : 'text.disabled', flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap title={t.title}>{t.title}</Typography>
                    <Typography variant="caption" color={overdue ? 'error.main' : 'text.secondary'}>
                      {days === null ? 'Nincs határidő'
                        : overdue ? `${Math.abs(days)} napja lejárt`
                          : days === 0 ? 'Ma esedékes'
                            : `${format(new Date(t.dueDate), 'MMM d.', { locale: hu })} · ${days} nap múlva`}
                    </Typography>
                  </Box>
                  {(t.priority === 'URGENT' || t.priority === 'HIGH') && (
                    <Chip size="small" variant="outlined" color={PRIORITY_COLOR[t.priority]} label={PRIORITY_LABEL[t.priority]} />
                  )}
                </Box>
              );
            })
          )}
        </SectionCard>

        <SectionCard title="Költségvetés kategóriánként" action={categories.length ? 'Részletek' : 'Új tétel'} onAction={() => onTabChange?.('budget')}>
          {categories.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              Még nincs költségvetési tétel.
            </Typography>
          ) : (
            categories.slice(0, 5).map((c, i) => (
              <Box key={c.name} sx={{ py: 1, borderTop: i ? `1px solid ${theme.palette.divider}` : 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }} title={c.name}>{c.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>{formatHufCompact(c.paid)}</Box>
                    {' / '}{formatHufCompact(c.budgeted)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={c.budgeted > 0 ? Math.min((c.paid / c.budgeted) * 100, 100) : 0}
                  sx={{ height: 4, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.10), '& .MuiLinearProgress-bar': { borderRadius: 2 } }}
                />
              </Box>
            ))
          )}
        </SectionCard>
      </Box>
    </Box>
  );
}

export default EventDashboardTab;
